import React, { useState, useEffect, useMemo } from 'react';
import { Check, Sunrise, RotateCcw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { CHECKIN_PILLARS, TOTAL_PROMPTS } from '../data/fourPillarsCheckIn';

const todayIso = () => new Date().toISOString().slice(0, 10);
const prettyDate = () =>
  new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

// Daily Four Pillars check-in. Ticks are stored per-day in localStorage
// (keyed to the user) and reset automatically when the date changes.
const FourPillarsCheckIn = () => {
  const { user } = useAuth();
  const storageKey = `gw_pillar_checkin_${user?.id || 'guest'}`;
  const [checked, setChecked] = useState({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        setChecked(saved.date === todayIso() ? saved.checked || {} : {});
      } else {
        setChecked({});
      }
    } catch {
      setChecked({});
    }
  }, [storageKey]);

  const persist = (next) => {
    setChecked(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify({ date: todayIso(), checked: next }));
    } catch {
      /* ignore quota errors */
    }
  };

  const toggle = (id) => {
    const next = { ...checked, [id]: !checked[id] };
    if (!next[id]) delete next[id];
    persist(next);
  };

  const doneCount = useMemo(() => Object.values(checked).filter(Boolean).length, [checked]);
  const pct = Math.round((doneCount / TOTAL_PROMPTS) * 100);

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 mb-6" data-testid="pillars-checkin">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="flex items-center gap-2">
          <Sunrise size={18} className="text-[#8a6a5a]" />
          <h2 className="font-heading text-2xl text-forest">Daily Check-In</h2>
        </div>
        {doneCount > 0 && (
          <button
            onClick={() => persist({})}
            className="inline-flex items-center gap-1 text-xs font-body text-stone-400 hover:text-stone-600"
            data-testid="checkin-reset"
          >
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>
      <p className="font-body text-sm text-stone-500 mb-1">{prettyDate()}</p>
      <p className="font-body text-sm text-stone-600 leading-relaxed mb-5">
        A gentle way to tune in — tick whatever is true for you today. Nothing to get perfect here; this is simply
        about noticing where your system is well-supported and where it's quietly asking for more.
      </p>

      {/* progress */}
      <div className="mb-6">
        <div className="flex justify-between items-baseline mb-1.5">
          <span className="font-body text-xs uppercase tracking-wider text-stone-400">Noticed today</span>
          <span className="font-body text-sm text-forest font-medium" data-testid="checkin-count">
            {doneCount} / {TOTAL_PROMPTS}
          </span>
        </div>
        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
          <div className="h-full bg-forest transition-all" style={{ width: `${pct}%` }} data-testid="checkin-bar" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
        {CHECKIN_PILLARS.map((pillar) => {
          const pillarDone = pillar.prompts.filter((_, i) => checked[`${pillar.key}-${i}`]).length;
          return (
            <div key={pillar.key} data-testid={`checkin-pillar-${pillar.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: pillar.dot }} />
                <h3 className="font-heading text-lg text-forest">{pillar.title}</h3>
                <span className="font-body text-xs text-stone-400">
                  {pillarDone}/{pillar.prompts.length}
                </span>
              </div>
              <ul className="space-y-2">
                {pillar.prompts.map((prompt, i) => {
                  const id = `${pillar.key}-${i}`;
                  const on = !!checked[id];
                  return (
                    <li key={id}>
                      <button
                        onClick={() => toggle(id)}
                        className="w-full flex items-start gap-3 text-left group"
                        data-testid={`checkin-prompt-${id}`}
                        aria-pressed={on}
                      >
                        <span
                          className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded-md border flex items-center justify-center transition-colors ${
                            on ? 'bg-forest border-forest' : 'bg-white border-stone-300 group-hover:border-forest/50'
                          }`}
                        >
                          {on && <Check size={13} className="text-cream" />}
                        </span>
                        <span
                          className={`font-body text-sm leading-snug transition-colors ${
                            on ? 'text-stone-400 line-through' : 'text-stone-700'
                          }`}
                        >
                          {prompt}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {doneCount === TOTAL_PROMPTS && (
        <p className="font-accent italic text-forest mt-6" data-testid="checkin-complete">
          Beautifully tended today. Remember — smoothness comes not from perfection, but from gently supporting the
          most strained areas. See you tomorrow.
        </p>
      )}
    </div>
  );
};

export default FourPillarsCheckIn;
